/**
 * 自由创作「时长」行(2026-09-13 D 批,设计稿 7d586023 V4 · FreeCreate 板)。
 *
 * ═══ 与目的流的时长档不是一回事 ═══════════════════════════════════════════════════
 * 目的流走**编排**(多镜),时长档 = 成片总时长(15/30/45/60s,写 `form.briefDuration`,
 * ai 侧把各镜之和钳到它)。自由创作走**直发**(单段一次 provider 调用),时长 = 这一段
 * 的秒数(5/10/15/30s,直接写 `form.durationSec`,bareSend 组包/报价/钳位都读它)。
 *
 * ═══ ★30s / 1080P 自动升引擎 —— 不硬编,靠能力表 ═══════════════════════════════════
 * 默认 2.0 Fast 单段最长 15s;选 30s(或 1080P)时 `autoPickModel` 会按 duration_range /
 * supportedResolutions 自动升到能出的档(现网即 Seedance 2.5,duration_range 4-30)。
 * 本组件**不判也不切模型**:切换在 `useVideoProviderConstraints`(智能选型按 durationSec +
 * resolution 收窄)。这里只在选了 ≥ 默认 Fast 上限的档时,把「已自动换成哪个引擎出片」
 * 如实写出来(`autoModelLabel` 由页面从 selectedCap.label 传入,不写死型号名)。
 */
import type { Dispatch, SetStateAction } from "react";

import type { FormState } from "../../model/videoCreateTypes";

import { OptionChip, OptionChipGroup } from "./OptionChip";

/** 自由创作单段时长档(秒)。5/10/15 走 2.0 Fast;30 需 2.5(见头注)。 */
export const CLIP_DURATION_OPTIONS: readonly { label: string; value: number }[] = [
  { label: "5s", value: 5 },
  { label: "10s", value: 10 },
  { label: "15s", value: 15 },
  { label: "30s", value: 30 },
];

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  disabled?: boolean;
  /** 2.0 Fast 单段上限(秒)—— 超过它的档会自动升引擎;拿不到能力表时按 15 兜底。 */
  fastMaxSec?: number;
  /** 当前实际会用的引擎人读名(selectedCap.label);升档时旁注用,拿不到不渲染那句。 */
  autoModelLabel?: string;
}

export function VideoClipDurationRow({
  form,
  setForm,
  disabled,
  fastMaxSec = 15,
  autoModelLabel,
}: Props) {
  const cur = form.durationSec;
  const upgraded = cur > fastMaxSec; // 选了超过 Fast 上限的档 → 已升引擎
  return (
    <div data-video-clip-duration-row>
      <span className="mb-1.5 block text-sm text-muted-foreground">时长</span>
      <OptionChipGroup label="单段时长">
        {CLIP_DURATION_OPTIONS.map((d) => {
          const active = cur === d.value;
          return (
            <OptionChip
              key={d.value}
              radio
              active={active}
              tabIndex={active ? 0 : -1}
              disabled={disabled}
              onClick={() => setForm((f) => ({ ...f, durationSec: d.value }))}
            >
              {d.label}
            </OptionChip>
          );
        })}
      </OptionChipGroup>
      {/* ★0914 拍板:自由流只直发,「想要多镜走智能编排」这句已跟着功能出口一起退役 ——
          未升档时不再有话要说(直发就是一段连续镜头,不需要每次都重申);
          升档才有值得说的事实(实际会换用哪个引擎出片)。 */}
      {upgraded && (
        <p
          data-testid="clip-duration-upgrade-note"
          className="mt-1.5 max-w-[26rem] text-2xs leading-snug text-muted-foreground/80"
        >
          {cur}s 是单段连续镜
          {autoModelLabel ? `,自动用 ${autoModelLabel} 出片` : "(自动升到更长的出片引擎)"}。
        </p>
      )}
    </div>
  );
}
